/**
 * PasswordReset.js — PeoplesDock
 *
 * ADDED: UI Validation
 *
 *   ForgotPassword:
 *     - Email: required, valid email format
 *     - isInvalid / Form.Control.Feedback inline under field
 *     - Validates on blur and on submit
 *     - Errors clear on change
 *
 *   ResetPassword:
 *     - Password: required, min 8 characters — inline error under field
 *     - Confirm: required, must match password — inline error under field
 *     - Validates on blur for both fields
 *     - Submit blocked until both pass
 *     - Strength bar: unchanged from your version
 *     - showPw / showCf toggle: unchanged
 *
 * Everything else identical to your version.
 */
import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Mail, Lock, CheckCircle, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';

// ── Shared wrapper ─────────────────────────────────────────────────────────────
const AuthCard = ({ icon: Icon, iconColor, title, subtitle, children }) => (
    <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg,#f8faff,#eef2ff)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
    }}>
        <Container style={{ maxWidth: 440 }}>
            {/* Logo */}
            <div className="text-center mb-4">
                <Link to="/">
                    <img src="/logo.png" alt="PeoplesDock" style={{ height: 70 }}/>
                </Link>
            </div>
            <Card className="border-0 shadow-sm" style={{ borderRadius: 20 }}>
                <Card.Body className="p-4 p-md-5">
                    {/* Icon */}
                    <div style={{
                        width: 64, height: 64, borderRadius: 16,
                        background: `${iconColor}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 20px',
                    }}>
                        <Icon size={28} color={iconColor}/>
                    </div>
                    <h4 className="fw-bold text-center mb-1">{title}</h4>
                    <p className="text-muted text-center small mb-4">{subtitle}</p>
                    {children}
                </Card.Body>
            </Card>
            <p className="text-center text-muted small mt-3">
                <Link to="/login"
                    className="d-inline-flex align-items-center gap-1 text-decoration-none"
                    style={{ color: '#4f46e5' }}>
                    <ArrowLeft size={14}/> Back to Login
                </Link>
            </p>
        </Container>
    </div>
);

// ════════════════════════════════════════════════════════════════════════════
// FORGOT PASSWORD PAGE — /forgot-password
// ════════════════════════════════════════════════════════════════════════════
export const ForgotPassword = () => {
    const [email,   setEmail]   = useState('');
    const [loading, setLoading] = useState(false);
    const [sent,    setSent]    = useState(false);
    const [error,   setError]   = useState('');           // API-level error (top Alert)
    const [emailErr, setEmailErr] = useState('');         // Field-level validation error
    const [touched,  setTouched] = useState(false);       // Has user blurred the field?

    // ── Validate email field ─────────────────────────────────────────────
    const validateEmail = (val) => {
        if (!val?.trim()) return 'Email address is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim())) return 'Enter a valid email address';
        return '';
    };

    const handleChange = (e) => {
        setEmail(e.target.value);
        if (emailErr) setEmailErr('');   // clear field error as user types
        if (error)    setError('');      // clear API error too
    };

    const handleBlur = () => {
        setTouched(true);
        setEmailErr(validateEmail(email));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched(true);
        const fieldErr = validateEmail(email);
        if (fieldErr) { setEmailErr(fieldErr); return; }

        setLoading(true); setError('');
        try {
            await axiosInstance.post('/auth/forgot-password', { email: email.trim() });
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally { setLoading(false); }
    };

    if (sent) return (
        <AuthCard icon={CheckCircle} iconColor="#059669"
            title="Check your inbox"
            subtitle={`We've sent a password reset link to ${email}`}>
            <Alert variant="success" className="small py-2 text-center">
                The link expires in <strong>1 hour</strong>.
                Check your spam folder if you don't see it.
            </Alert>
            <Button variant="outline-primary" className="w-100 rounded-pill mt-2"
                onClick={() => { setSent(false); setTouched(false); setEmailErr(''); }}>
                Try a different email
            </Button>
        </AuthCard>
    );

    return (
        <AuthCard icon={Mail} iconColor="#4f46e5"
            title="Forgot your password?"
            subtitle="Enter your work email and we'll send you a reset link.">
            {/* API-level error */}
            {error && (
                <Alert variant="danger" className="small py-2 d-flex align-items-center gap-2">
                    <AlertCircle size={14}/> {error}
                </Alert>
            )}
            <Form onSubmit={handleSubmit} noValidate>
                <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Work Email</Form.Label>
                    <Form.Control
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus
                        isInvalid={!!(touched && emailErr)}
                        isValid={!!(touched && !emailErr && email)}
                        style={{ borderRadius: 10, padding: '12px 14px' }}
                    />
                    <Form.Control.Feedback type="invalid">
                        {emailErr}
                    </Form.Control.Feedback>
                </Form.Group>
                <Button type="submit" variant="primary"
                    className="w-100 rounded-pill py-3 fw-bold"
                    disabled={loading}
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                    {loading ? <Spinner size="sm" animation="border"/> : 'Send Reset Link →'}
                </Button>
            </Form>
        </AuthCard>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// RESET PASSWORD PAGE — /reset-password/:token
// ════════════════════════════════════════════════════════════════════════════
export const ResetPassword = () => {
    const { token }  = useParams();
    const navigate   = useNavigate();

    const [password,   setPassword]   = useState('');
    const [confirm,    setConfirm]    = useState('');
    const [showPw,     setShowPw]     = useState(false);
    const [showCf,     setShowCf]     = useState(false);
    const [loading,    setLoading]    = useState(false);
    const [checking,   setChecking]   = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [done,       setDone]       = useState(false);
    const [error,      setError]      = useState('');     // API-level error (top Alert)

    // Field-level validation errors
    const [pwErr,  setPwErr]  = useState('');
    const [cfErr,  setCfErr]  = useState('');
    // Touched flags
    const [pwTouched, setPwTouched] = useState(false);
    const [cfTouched, setCfTouched] = useState(false);

    // ── Validate fields ──────────────────────────────────────────────────
    const validatePassword = (val) => {
        if (!val) return 'Password is required';
        if (val.length < 8) return 'Password must be at least 8 characters';
        return '';
    };
    const validateConfirm = (val, pw) => {
        if (!val) return 'Please confirm your password';
        if (val !== pw) return 'Passwords do not match';
        return '';
    };

    // Validate token on mount
    useEffect(() => {
        if (!token) { setChecking(false); return; }
        axiosInstance.get(`/auth/reset-password/${token}/check`)
            .then(r => setTokenValid(r.data.data?.valid === true))
            .catch(() => setTokenValid(false))
            .finally(() => setChecking(false));
    }, [token]);

    // ── Strength indicator — unchanged from your version ─────────────────
    const strength = !password ? 0
        : password.length >= 12 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password) ? 4
        : password.length >= 10 && /[A-Z]/.test(password) && /[0-9]/.test(password) ? 3
        : password.length >= 8 ? 2
        : 1;
    const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    const strengthColor = ['', '#ef4444', '#f59e0b', '#3b82f6', '#059669'];

    // ── Handlers ─────────────────────────────────────────────────────────
    const handlePasswordChange = (e) => {
        const val = e.target.value;
        setPassword(val);
        if (pwErr) setPwErr('');
        if (error) setError('');
        // If confirm already touched, re-validate it live as password changes
        if (cfTouched) setCfErr(validateConfirm(confirm, val));
    };

    const handleConfirmChange = (e) => {
        const val = e.target.value;
        setConfirm(val);
        if (cfErr) setCfErr('');
        if (error) setError('');
    };

    const handlePasswordBlur = () => {
        setPwTouched(true);
        setPwErr(validatePassword(password));
    };

    const handleConfirmBlur = () => {
        setCfTouched(true);
        setCfErr(validateConfirm(confirm, password));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setPwTouched(true); setCfTouched(true);
        const pErr = validatePassword(password);
        const cErr = validateConfirm(confirm, password);
        if (pErr) { setPwErr(pErr); return; }
        if (cErr) { setCfErr(cErr); return; }

        setLoading(true); setError('');
        try {
            await axiosInstance.post(`/auth/reset-password/${token}`, {
                new_password: password,
            });
            setDone(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally { setLoading(false); }
    };

    // ── State renders ─────────────────────────────────────────────────────
    if (checking) return (
        <AuthCard icon={Lock} iconColor="#4f46e5" title="Checking link…" subtitle="">
            <div className="text-center py-3"><Spinner animation="border" variant="primary"/></div>
        </AuthCard>
    );

    if (!tokenValid) return (
        <AuthCard icon={AlertCircle} iconColor="#ef4444"
            title="Link expired"
            subtitle="This password reset link has expired or already been used.">
            <Button as={Link} to="/forgot-password" variant="primary"
                className="w-100 rounded-pill fw-bold"
                style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                Request a new link
            </Button>
        </AuthCard>
    );

    if (done) return (
        <AuthCard icon={CheckCircle} iconColor="#059669"
            title="Password updated!"
            subtitle="Redirecting you to login in a moment…">
            <Alert variant="success" className="text-center small">
                ✓ Your password has been changed successfully.
            </Alert>
            <Button as={Link} to="/login" variant="primary"
                className="w-100 rounded-pill fw-bold mt-2"
                style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                Go to Login →
            </Button>
        </AuthCard>
    );

    return (
        <AuthCard icon={Lock} iconColor="#4f46e5"
            title="Set new password"
            subtitle="Choose a strong password for your PeoplesDock account.">
            {/* API-level error */}
            {error && (
                <Alert variant="danger" className="small py-2 d-flex align-items-center gap-2">
                    <AlertCircle size={14}/> {error}
                </Alert>
            )}
            <Form onSubmit={handleSubmit} noValidate>
                {/* New Password */}
                <Form.Group className="mb-2">
                    <Form.Label className="small fw-bold">New Password</Form.Label>
                    <div className="position-relative">
                        <Form.Control
                            type={showPw ? 'text' : 'password'}
                            placeholder="At least 8 characters"
                            value={password}
                            onChange={handlePasswordChange}
                            onBlur={handlePasswordBlur}
                            autoFocus
                            isInvalid={!!(pwTouched && pwErr)}
                            isValid={!!(pwTouched && !pwErr && password)}
                            style={{ borderRadius: 10, padding: '12px 44px 12px 14px' }}
                        />
                        <span onClick={() => setShowPw(p => !p)} style={{
                            position: 'absolute', right: 14, top: '50%',
                            transform: 'translateY(-50%)', cursor: 'pointer', color: '#9ca3af',
                            zIndex: 10,
                        }}>
                            {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                        </span>
                        <Form.Control.Feedback type="invalid">
                            {pwErr}
                        </Form.Control.Feedback>
                    </div>
                    {/* Strength bar — unchanged from your version */}
                    {password && (
                        <div className="mt-2">
                            <div className="d-flex gap-1 mb-1">
                                {[1,2,3,4].map(i => (
                                    <div key={i} style={{
                                        flex: 1, height: 4, borderRadius: 4,
                                        background: i <= strength ? strengthColor[strength] : '#e5e7eb',
                                        transition: 'background 0.2s',
                                    }}/>
                                ))}
                            </div>
                            <span style={{ fontSize: 11, color: strengthColor[strength], fontWeight: 600 }}>
                                {strengthLabel[strength]}
                            </span>
                        </div>
                    )}
                </Form.Group>

                {/* Confirm Password */}
                <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold">Confirm Password</Form.Label>
                    <div className="position-relative">
                        <Form.Control
                            type={showCf ? 'text' : 'password'}
                            placeholder="Repeat new password"
                            value={confirm}
                            onChange={handleConfirmChange}
                            onBlur={handleConfirmBlur}
                            isInvalid={!!(cfTouched && cfErr)}
                            isValid={!!(cfTouched && !cfErr && confirm)}
                            style={{ borderRadius: 10, padding: '12px 44px 12px 14px' }}
                        />
                        <span onClick={() => setShowCf(p => !p)} style={{
                            position: 'absolute', right: 14, top: '50%',
                            transform: 'translateY(-50%)', cursor: 'pointer', color: '#9ca3af',
                            zIndex: 10,
                        }}>
                            {showCf ? <EyeOff size={16}/> : <Eye size={16}/>}
                        </span>
                        <Form.Control.Feedback type="invalid">
                            {cfErr}
                        </Form.Control.Feedback>
                    </div>
                </Form.Group>

                <Button type="submit" variant="primary"
                    className="w-100 rounded-pill py-3 fw-bold"
                    disabled={loading}
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                    {loading ? <Spinner size="sm" animation="border"/> : 'Update Password →'}
                </Button>
            </Form>
        </AuthCard>
    );
};

export default ForgotPassword;
